import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { X } from 'lucide-react';
import WalletIcon from '../svgs/WalletIcon';
import CashPayoutContent from './cash_payout_content';
import PippCreditContent from './pipp_credit_content';
import { useTokenTranslate } from '../../contexts/token_translation_context';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 20px 0;
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
`;

const Title = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: #2a1a6e;
  margin-left: 10px;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #2a1a6e;
  display: flex;
  align-items: center;
  border-radius: 6px;
  &:hover { background: #f0ecff; }
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #999;
  margin: 0 0 12px;
  line-height: 1.4;
`;

const TabsContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: #f4f4f6;
  border-radius: 36px;
  padding: 4px;
  width: 280px;
  margin: 0 auto 14px;
  height: 40px;
`;

const TabIndicator = styled.span`
  position: absolute;
  top: 4px;
  height: calc(100% - 8px);
  border-radius: 28px;
  background: #e0d4ff;
  transition: transform 200ms ease, width 200ms ease;
  pointer-events: none;
`;

const TabButton = styled.button`
  position: relative;
  z-index: 1;
  flex: 1;
  border: none;
  background: none;
  border-radius: 28px;
  font-size: 14px;
  font-weight: ${props => props.active ? '700' : '500'};
  color: ${props => props.active ? '#2a1a6e' : '#888'};
  cursor: pointer;
  height: 100%;
  transition: color 150ms;
`;

const tabFadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const skeletonStyle = css`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 6px;
  animation: ${shimmer} 1.4s infinite;
`;

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SkeletonCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SkeletonLabel = styled.div`
  ${skeletonStyle}
  height: 12px;
  width: 80px;
  margin-bottom: 16px;
`;

const SkeletonBalance = styled.div`
  ${skeletonStyle}
  height: 72px;
  width: 160px;
  border-radius: 8px;
`;

const SkeletonDivider = styled.div`
  border-bottom: 1px dashed #ddd;
  margin: 4px 0 12px;
`;

const SkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SkeletonRow = styled.div`
  ${skeletonStyle}
  height: 12px;
  width: ${props => props.skWidth || '60%'};
`;

const SKELETON_ROWS = [
  { key: 'sk-0', width: '55%' },
  { key: 'sk-1', width: '40%' },
  { key: 'sk-2', width: '65%' }
];

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 16px;
  min-height: 0;
`;

const TabPane = styled.div`
  animation: ${tabFadeIn} 200ms ease;
  height: 95%;
`;

const ErrorBanner = styled.div`
  margin: 0 20px 8px;
  padding: 8px 12px;
  background: #fff0f0;
  border-radius: 8px;
  font-size: 13px;
  color: #c0392b;
`;

const WalletContent = ({
  userName, balance, currency, credits,
  cashPayoutSummary, errorMessage, loading, onClose, onCheckAllPayouts, onCheckAllCredits
}) => {
  const { translate, locale } = useTokenTranslate();
  const [activeTab, setActiveTab] = useState('cash');
  const tabsRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({});

  useEffect(() => {
    const container = tabsRef.current;
    if (!container) return;
    const btn = container.querySelector(`button[data-tab="${activeTab}"]`);
    if (!btn) return;
    const containerRect = container.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setIndicatorStyle({
      transform: `translateX(${btnRect.left - containerRect.left}px)`,
      width: btn.offsetWidth
    });
  }, [activeTab]);

  const handleCashTab = useCallback(() => setActiveTab('cash'), []);
  const handleCreditTab = useCallback(() => setActiveTab('credit'), []);

  return (
    <Wrap>
      <Header>
        <HeaderRow>
          <WalletIcon size={26} color='#2a1a6e' />
          <Title>
            {locale.startsWith('ar')
              ? `${userName} ${translate('web.wallet_modal.your_wallet_title')}`
              : `${userName}'s ${translate('web.wallet_modal.your_wallet_title')}`}
          </Title>
          <CloseButton onClick={onClose} aria-label='Close wallet'>
            <X size={20} />
          </CloseButton>
        </HeaderRow>
        <Subtitle>{translate('web.wallet_modal.earnings_info_sub_title')}</Subtitle>

        <TabsContainer ref={tabsRef}>
          <TabIndicator style={indicatorStyle} />
          <TabButton data-tab='cash' active={activeTab === 'cash'} onClick={handleCashTab}>
            {translate('web.wallet_tabs.cash_payout_button')}
          </TabButton>
          <TabButton data-tab='credit' active={activeTab === 'credit'} onClick={handleCreditTab}>
            {translate('web.wallet_tabs.pip_credit_button')}
          </TabButton>
        </TabsContainer>
      </Header>

      {errorMessage && <ErrorBanner>{errorMessage}</ErrorBanner>}

      <TabContent>
        {loading
          ? (
            <SkeletonWrap>
              <SkeletonCenter>
                <SkeletonLabel />
                <SkeletonBalance />
              </SkeletonCenter>
              <SkeletonDivider />
              <SkeletonRows>
                {SKELETON_ROWS.map(({ key, width }) => (
                  <SkeletonRow key={key} skWidth={width} />
                ))}
              </SkeletonRows>
            </SkeletonWrap>
          )
          : (
            <TabPane key={activeTab}>
              {activeTab === 'cash'
                ? <CashPayoutContent balance={balance}
                    currency={currency}
                    summaryData={cashPayoutSummary}
                    onCheckAllPayouts={onCheckAllPayouts} />
                : <PippCreditContent userName={userName}
                    credits={credits}
                    onCheckAllCredits={onCheckAllCredits} />}
            </TabPane>
          )}
      </TabContent>
    </Wrap>
  );
};

export default WalletContent;
